import { useEffect, useRef, useState } from "react";
import { Send, Loader2, Mic, Square, Volume2, VolumeX, CalendarDays, MessageSquarePlus, CheckCircle2, Phone } from "lucide-react";
import {
  receptionistInfo, receptionistChat, receptionistVoice, receptionistSpeak,
  receptionistTakeMessage, receptionistBook, receptionistAvailability,
} from "@/lib/api";

// A live test console for a receptionist (chat + voice + take-message + book).
// Hits the PUBLIC runtime endpoints — the same engine that Phase 1b telephony will use.
export function ReceptionistTestConsole({ receptionistId }) {
  const [info, setInfo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [voiceOn, setVoiceOn] = useState(false);
  const [recording, setRecording] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [panel, setPanel] = useState("chat"); // chat | message | book
  const [transfer, setTransfer] = useState(null);
  const [msgForm, setMsgForm] = useState({ caller_name: "", callback: "", reason: "", urgency: "normal" });
  const [bookForm, setBookForm] = useState({ name: "", email: "", phone: "", date: "", time: "", service: "", notes: "" });
  const [actionBusy, setActionBusy] = useState(false);
  const [actionErr, setActionErr] = useState("");
  const [done, setDone] = useState("");
  const [calendar, setCalendar] = useState(null);
  const [slots, setSlots] = useState(null); // null=not loaded, []=none

  const scrollRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const sessionRef = useRef(`rcp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

  useEffect(() => {
    setMessages([]); setDone(""); setPanel("chat"); setTransfer(null); setCalendar(null);
    sessionRef.current = `rcp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    receptionistInfo(receptionistId).then((d) => {
      setInfo(d);
      setMessages([{ role: "assistant", content: d.greeting }]);
    }).catch(() => setInfo(null));
  }, [receptionistId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const speak = async (text) => {
    try {
      const { audio_base64, mime } = await receptionistSpeak(receptionistId, text);
      if (audioRef.current) audioRef.current.pause();
      const el = new Audio(`data:${mime};base64,${audio_base64}`);
      audioRef.current = el;
      el.play().catch(() => {});
    } catch { /* best-effort */ }
  };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((m) => [...m, { role: "user", content: q }]);
    setInput("");
    setBusy(true);
    try {
      const res = await receptionistChat(receptionistId, q, history, sessionRef.current);
      setMessages((m) => [...m, { role: "assistant", content: res.reply }]);
      if (res.transfer) setTransfer(res.transfer_number || true);
      if (voiceOn) speak(res.reply);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — I had trouble responding. Please try again." }]);
    } finally { setBusy(false); }
  };

  const startRecording = async () => {
    if (recording || busy || voiceBusy) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 800) { setVoiceBusy(false); return; }
        setVoiceBusy(true);
        const history = messages.map((m) => ({ role: m.role, content: m.content }));
        const fd = new FormData();
        fd.append("session_id", sessionRef.current);
        fd.append("history", JSON.stringify(history));
        fd.append("audio", blob, "clip.webm");
        try {
          const res = await receptionistVoice(receptionistId, fd);
          const transcript = (res.transcript || "").trim();
          if (transcript.length < 2) {
            setMessages((m) => [...m, { role: "assistant", content: "I couldn't quite catch that — please try again or type your message." }]);
          } else {
            setMessages((m) => [...m, { role: "user", content: transcript }, { role: "assistant", content: res.reply }]);
            if (voiceOn) speak(res.reply);
          }
        } catch {
          setMessages((m) => [...m, { role: "assistant", content: "Sorry — I couldn't catch that. Please try again or type." }]);
        } finally { setVoiceBusy(false); }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "I couldn't access your microphone. Please allow mic access or type your message." }]);
    }
  };
  const stopRecording = () => { if (recorderRef.current && recording) { recorderRef.current.stop(); setRecording(false); } };

  const submitMessage = async () => {
    setActionErr("");
    const { caller_name, callback, reason } = msgForm;
    if (!caller_name.trim() || !callback.trim() || !reason.trim()) { setActionErr("Please fill in name, callback and reason."); return; }
    setActionBusy(true);
    try {
      const res = await receptionistTakeMessage(receptionistId, { ...msgForm, session_id: sessionRef.current });
      setDone(res.confirmation);
      setMessages((m) => [...m, { role: "assistant", content: res.confirmation }]);
      setMsgForm({ caller_name: "", callback: "", reason: "", urgency: "normal" });
      setPanel("chat");
    } catch (e) {
      setActionErr(e?.response?.data?.detail || "Could not take the message.");
    } finally { setActionBusy(false); }
  };

  const loadSlots = async (date) => {
    setSlots(null);
    setBookForm((f) => ({ ...f, date, time: "" }));
    if (!date) return;
    try { const a = await receptionistAvailability(receptionistId, date); setSlots(a.slots || []); }
    catch { setSlots([]); }
  };

  const submitBook = async () => {
    setActionErr("");
    const { name, email, phone, date, time } = bookForm;
    if (!name.trim() || (!email.trim() && !phone.trim()) || !date || !time) { setActionErr("Please fill in name, an email or phone, date and time."); return; }
    setActionBusy(true);
    try {
      const res = await receptionistBook(receptionistId, {
        name: name.trim(), email: email.trim() || null, phone: phone.trim() || null,
        date, time, service: bookForm.service.trim() || null, notes: bookForm.notes.trim() || null,
        session_id: sessionRef.current,
      });
      setDone(res.confirmation);
      setCalendar(res.calendar || null);
      setMessages((m) => [...m, { role: "assistant", content: res.confirmation }]);
      setBookForm({ name: "", email: "", phone: "", date: "", time: "", service: "", notes: "" });
      setPanel("chat");
    } catch (e) {
      setActionErr(e?.response?.data?.detail || "Could not submit the booking.");
    } finally { setActionBusy(false); }
  };

  if (!info) return <div className="p-6 text-sm text-slate-500" data-testid="console-loading">Loading test console…</div>;
  if (!info.active) return (
    <div className="p-6 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="console-inactive">
      This receptionist is currently <strong>inactive</strong>. Turn it on in Setup to test it live.
    </div>
  );

  const field = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <div className="rounded-2xl border border-slate-200 overflow-hidden bg-white" data-testid="receptionist-test-console">
      <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-white">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Phone className="w-4 h-4 text-blue-400" /> {info.name} · {info.business_name}
        </div>
        <button
          onClick={() => setVoiceOn((v) => !v)}
          data-testid="console-voice-toggle"
          className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        >
          {voiceOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
          {voiceOn ? "Voice on" : "Voice off"}
        </button>
      </div>

      <div ref={scrollRef} className="h-72 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50" data-testid="console-transcript">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[82%] px-3.5 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-blue-600 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"}`}>
              {m.content}
            </div>
          </div>
        ))}
        {(busy || voiceBusy) && (
          <div className="flex justify-start"><div className="px-3.5 py-2 rounded-2xl bg-white border border-slate-200"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div></div>
        )}
      </div>

      {panel === "chat" && (
        <>
          {info.prompts?.length > 0 && messages.length <= 1 && (
            <div className="flex flex-wrap gap-2 px-4 pt-3">
              {info.prompts.map((p, i) => (
                <button key={i} onClick={() => send(p)} data-testid={`console-prompt-${i}`} className="text-xs px-3 py-1.5 rounded-full border border-slate-300 text-slate-600 hover:border-blue-500 hover:text-blue-600 transition-colors">{p}</button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 px-4 py-3 border-t border-slate-100">
            <button onClick={() => setPanel("message")} data-testid="console-open-message" title="Take a message" className="p-2 rounded-lg border border-slate-300 text-slate-600 hover:border-blue-500 hover:text-blue-600"><MessageSquarePlus className="w-4 h-4" /></button>
            <button onClick={() => setPanel("book")} data-testid="console-open-book" title="Book appointment" className="p-2 rounded-lg border border-slate-300 text-slate-600 hover:border-blue-500 hover:text-blue-600"><CalendarDays className="w-4 h-4" /></button>
            <input
              value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type a caller message…" data-testid="console-input" className={field}
            />
            <button onClick={recording ? stopRecording : startRecording} data-testid="console-mic" title="Tap to talk" className={`p-2 rounded-lg border ${recording ? "bg-red-600 text-white border-red-600 animate-pulse" : "border-slate-300 text-slate-600 hover:border-blue-500 hover:text-blue-600"}`}>
              {recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
            <button onClick={() => send()} disabled={busy} data-testid="console-send" className="p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"><Send className="w-4 h-4" /></button>
          </div>
        </>
      )}

      {panel === "message" && (
        <div className="p-4 border-t border-slate-100 space-y-2" data-testid="console-message-form">
          <div className="text-sm font-semibold text-slate-800">Take a message</div>
          <input className={field} placeholder="Caller name" value={msgForm.caller_name} onChange={(e) => setMsgForm({ ...msgForm, caller_name: e.target.value })} data-testid="message-name" />
          <input className={field} placeholder="Callback (phone or email)" value={msgForm.callback} onChange={(e) => setMsgForm({ ...msgForm, callback: e.target.value })} data-testid="message-callback" />
          <textarea className={field} rows={2} placeholder="Reason for the call" value={msgForm.reason} onChange={(e) => setMsgForm({ ...msgForm, reason: e.target.value })} data-testid="message-reason" />
          <select className={field} value={msgForm.urgency} onChange={(e) => setMsgForm({ ...msgForm, urgency: e.target.value })} data-testid="message-urgency">
            <option value="low">Low urgency</option><option value="normal">Normal</option><option value="high">High urgency</option>
          </select>
          {actionErr && <div className="text-xs text-red-600" data-testid="console-error">{actionErr}</div>}
          <div className="flex gap-2 pt-1">
            <button onClick={submitMessage} disabled={actionBusy} data-testid="message-submit" className="flex-1 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">{actionBusy ? "Sending…" : "Send message"}</button>
            <button onClick={() => { setPanel("chat"); setActionErr(""); }} className="px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-600">Cancel</button>
          </div>
        </div>
      )}

      {panel === "book" && (
        <div className="p-4 border-t border-slate-100 space-y-2" data-testid="console-book-form">
          <div className="text-sm font-semibold text-slate-800">Book an appointment</div>
          <input className={field} placeholder="Name" value={bookForm.name} onChange={(e) => setBookForm({ ...bookForm, name: e.target.value })} data-testid="book-name" />
          <div className="flex gap-2">
            <input className={field} placeholder="Email (optional)" value={bookForm.email} onChange={(e) => setBookForm({ ...bookForm, email: e.target.value })} data-testid="book-email" />
            <input className={field} placeholder="Phone (optional)" value={bookForm.phone} onChange={(e) => setBookForm({ ...bookForm, phone: e.target.value })} data-testid="book-phone" />
          </div>
          <p className="text-[11px] text-slate-400">No email? We'll text the confirmation and reminder to the phone number instead.</p>
          <div className="flex gap-2">
            <input type="date" className={field} value={bookForm.date} onChange={(e) => loadSlots(e.target.value)} data-testid="book-date" />
            <select className={field} value={bookForm.time} onChange={(e) => setBookForm({ ...bookForm, time: e.target.value })} data-testid="book-time" disabled={!bookForm.date}>
              {!bookForm.date && <option value="">Pick a date</option>}
              {bookForm.date && slots === null && <option value="">Loading times…</option>}
              {bookForm.date && slots !== null && slots.length === 0 && <option value="">No open slots</option>}
              {bookForm.date && slots !== null && slots.length > 0 && <option value="">Choose a time</option>}
              {(slots || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <input className={field} placeholder="Service (optional)" value={bookForm.service} onChange={(e) => setBookForm({ ...bookForm, service: e.target.value })} data-testid="book-service" />
          <textarea className={field} rows={2} placeholder="Notes (optional)" value={bookForm.notes} onChange={(e) => setBookForm({ ...bookForm, notes: e.target.value })} data-testid="book-notes" />
          {actionErr && <div className="text-xs text-red-600" data-testid="console-error">{actionErr}</div>}
          <div className="flex gap-2 pt-1">
            <button onClick={submitBook} disabled={actionBusy} data-testid="book-submit" className="flex-1 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">{actionBusy ? "Booking…" : "Request booking"}</button>
            <button onClick={() => { setPanel("chat"); setActionErr(""); }} className="px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-600">Cancel</button>
          </div>
        </div>
      )}

      {transfer && (
        <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 text-xs border-t border-blue-100" data-testid="console-transfer">
          <Phone className="w-4 h-4" /> Warm transfer — connecting the caller to a person{typeof transfer === "string" ? ` at ${transfer}` : ""}. (On a real phone call this dials your transfer number.)
        </div>
      )}
      {done && (
        <div className="px-4 py-2 bg-emerald-50 text-emerald-700 text-xs border-t border-emerald-100" data-testid="console-done">
          <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> {done}</div>
          {calendar && (
            <div className="flex gap-3 mt-1.5 pl-6" data-testid="console-calendar">
              <a href={calendar.google} target="_blank" rel="noreferrer" className="underline hover:text-emerald-800">Add to Google Calendar</a>
              <a href={calendar.ics} target="_blank" rel="noreferrer" className="underline hover:text-emerald-800">Download .ics</a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
